import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, X, Image as ImageIcon } from 'lucide-react'
import toast from 'react-hot-toast'

const MultiImageUpload = ({
    onUpload,
    images = [],
    onRemove,
    maxSize = 5 * 1024 * 1024, // 5MB
    maxFiles = 10,
    className = ''
}) => {
    const [uploading, setUploading] = useState(false)

    const onDrop = useCallback(async (acceptedFiles) => {
        if (images.length + acceptedFiles.length > maxFiles) {
            toast.error(`Maksimal ${maxFiles} gambar yang diperbolehkan`)
            return
        }

        setUploading(true)
        try {
            for (const file of acceptedFiles) {
                if (file.size > maxSize) {
                    toast.error(`File ${file.name} terlalu besar. Maksimal 5MB`)
                    continue
                }

                // Validasi tipe file
                if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
                    toast.error(`File ${file.name} harus berupa JPG, PNG, GIF atau WEBP`)
                    continue
                }

                const formData = new FormData()
                formData.append('image', file)
                await onUpload(formData)
            }
        } catch (error) {
            console.error('Upload error:', error)
            toast.error('Gagal mengunggah gambar: ' + (error.message || 'Unknown error'))
        } finally {
            setUploading(false)
        }
    }, [onUpload, maxSize, images.length, maxFiles])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/gif': ['.gif'],
            'image/webp': ['.webp']
        },
        maxFiles,
        disabled: uploading,
        multiple: true
    })

    return (
        <div className={`space-y-4 ${className}`}>
            <div className="flex gap-2 flex-wrap">
                {images.map((img, idx) => (
                    <div key={idx} className="relative group">
                        <img 
                            src={img} 
                            alt={`Upload ${idx + 1}`} 
                            className="w-24 h-24 object-cover rounded border"
                            onError={(e) => {
                                e.target.src = '/placeholder-image.png' // Gambar placeholder
                                console.error(`Failed to load image: ${img}`)
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => onRemove(idx)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full 
                                     opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Hapus gambar"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>

            <div
                {...getRootProps()}
                className={`
                    border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                    ${isDragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'}
                    ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
                `}
            >
                <input {...getInputProps()} />
                <div className="space-y-2">
                    {uploading ? (
                        <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        </div>
                    ) : (
                        <>
                            <div className="flex justify-center">
                                {isDragActive ? (
                                    <Upload className="w-12 h-12 text-primary-500" />
                                ) : (
                                    <ImageIcon className="w-12 h-12 text-gray-400" />
                                )}
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">
                                    {isDragActive
                                        ? 'Lepaskan gambar di sini...'
                                        : 'Tarik & lepas gambar di sini, atau klik untuk memilih'}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    PNG, JPG, GIF atau WEBP (Maks. 5MB per file)
                                </p>
                                <p className="text-xs text-gray-500">
                                    Maksimal {maxFiles} gambar
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default MultiImageUpload